import { PopperPlacementType } from "@mui/base/Popper";
import {
  textInputOperators,
  valuelessOperators,
} from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { memo } from "react";
import type { AdvancedTableFilter } from "../advanced-filter";
import { ArrayStringValueDropdown } from "./array-to-string-filter";
import { BooleanValueDropdown } from "./boolean-filter";
import { cascadeSubmenuOffsetY } from "./cascade-submenu-alignment";
import { DateValueDropdown } from "./date-filter";
import { ExistsValueDropdown } from "./exists-filter";

/**
 * Step 2 of the deferred-pill add-filter flow: the value editor for a pending
 * (not-yet-committed) filter, rendered without a pill chip so a value can be
 * chosen before the pill is created. Shared by table2 and the support-v2 toolbar.
 */
export const FilterValueEditor = memo(function FilterValueEditor({
  filter,
  setFilter,
  filters,
  anchor,
  alignWithAnchorRow,
  open,
  setOpen,
  placement,
}: {
  filter: AdvancedTableFilter;
  setFilter(filter: AdvancedTableFilter): void;
  filters: AdvancedTableFilter[];
  anchor: HTMLElement | null;
  /** Sit the editor's first line of text on the anchor row's, for cascade submenus. */
  alignWithAnchorRow?: boolean;
  open: boolean;
  setOpen(open: boolean): void;
  placement?: PopperPlacementType;
}) {
  const listRowOffsetY = alignWithAnchorRow
    ? cascadeSubmenuOffsetY("listRow")
    : undefined;
  switch (filter.type) {
    case AdvancedFilterType.ARRAY_TO_STRING:
      return (
        <ArrayStringValueDropdown
          alignWithAnchorRow={alignWithAnchorRow}
          anchor={anchor}
          filter={filter}
          filters={filters}
          open={open}
          placement={placement}
          setFilter={setFilter}
          setOpen={setOpen}
        />
      );
    case AdvancedFilterType.BOOLEAN:
      return (
        <BooleanValueDropdown
          anchor={anchor}
          filter={filter}
          offsetY={listRowOffsetY}
          open={open}
          placement={placement}
          setFilter={setFilter}
          setOpen={setOpen}
        />
      );
    case AdvancedFilterType.EXISTS:
      return (
        <ExistsValueDropdown
          anchor={anchor}
          filter={filter}
          offsetY={listRowOffsetY}
          open={open}
          placement={placement}
          setFilter={setFilter}
          setOpen={setOpen}
        />
      );
    case AdvancedFilterType.DATE:
      return (
        <DateValueDropdown
          anchor={anchor}
          filter={filter}
          offsetY={listRowOffsetY}
          open={open}
          placement={placement}
          setFilter={setFilter}
          setOpen={setOpen}
        />
      );
    default:
      return null;
  }
});

/**
 * Whether `FilterValueEditor` renders a value editor for the given filter type.
 * Keep in sync with the switch above — these are exactly the cases it renders.
 */
export function isFilterValueEditorSupported(
  type: AdvancedFilterType,
): boolean {
  switch (type) {
    case AdvancedFilterType.ARRAY_TO_STRING:
    case AdvancedFilterType.BOOLEAN:
    case AdvancedFilterType.EXISTS:
    case AdvancedFilterType.DATE:
      return true;
    default:
      return false;
  }
}

/**
 * Whether a filter is worth cascading into, i.e. its submenu offers a list of
 * options to pick from. Callers (e.g. table2) give the rest a click-to-add pill,
 * which the user fills in on the pill itself.
 */
export function canCascadeIntoFilterValueEditor(
  filter: AdvancedTableFilter,
): boolean {
  if (!isFilterValueEditorSupported(filter.type)) {
    return false;
  }
  if (filter.type !== AdvancedFilterType.ARRAY_TO_STRING) {
    return true;
  }
  const { operator } = filter.data;
  // A typed value needs no submenu, and a valueless operator has nothing to
  // offer — as does a set operator with no options to list, whose submenu could
  // only ever say "no results".
  if (
    textInputOperators.includes(operator) ||
    valuelessOperators.includes(operator)
  ) {
    return false;
  }
  return !!filter.valueFetcher || !!filter.values?.length;
}
